import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { APP_VERSION, VERSION_POLL_MS, fetchDeployedVersion, isNewerDeployed } from "../lib/version.ts";

/**
 * The "a new version is available" banner (stale-bundle safeguard).
 *
 * A tab opened before a deploy keeps running the old bundle; a soft reload does not reliably pull
 * the new app because `index.html` revalidates while hashed assets cache forever. This polls the
 * BFF's `GET /version` (served `no-store`) and, when the deployed build id differs from the one
 * baked into this bundle ({@link APP_VERSION}), offers a one-click reload. It NEVER reloads on its
 * own — an operator mid-task is not interrupted — and it is a polite live region (text + action, not
 * colour alone; design 12.4). Any check failure is ignored, so a network blip never nags.
 *
 * The check also runs when the tab regains focus/visibility, which is when an operator returning to
 * a long-lived tab is most likely to be on a stale build.
 */
export function UpdateBanner(): ReactNode {
  const outdated = useUpdateAvailable();
  const [dismissed, setDismissed] = useState(false);

  // A dismissal applies to the CURRENT deployed version only; a still-newer deploy shows it again.
  const shownForRef = useRef<string>("");
  useEffect(() => {
    if (outdated !== null && outdated !== shownForRef.current) {
      shownForRef.current = outdated;
      setDismissed(false);
    }
  }, [outdated]);

  if (outdated === null || dismissed) return null;

  return (
    <div className="update-banner" role="status" aria-live="polite">
      <span aria-hidden="true">↻</span>
      <span className="update-banner__text">
        <strong>A new version is available.</strong> Reload to get the latest build; your work on
        this page is unaffected.
      </span>
      <button type="button" className="control__button" onClick={() => window.location.reload()}>
        Reload
      </button>
      <button
        type="button"
        className="update-banner__dismiss"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss the update notice"
      >
        Later
      </button>
    </div>
  );
}

/**
 * The deployed build id when it is NEWER than this bundle, else `null`. Polls on an interval and on
 * tab visibility/focus. Pure effect wiring; the comparison lives in `lib/version.ts`.
 */
function useUpdateAvailable(): string | null {
  const [deployed, setDeployed] = useState<string | null>(null);

  const check = useCallback(async (signal?: AbortSignal) => {
    const live = await fetchDeployedVersion(signal);
    if (live !== null && isNewerDeployed(APP_VERSION, live)) setDeployed(live);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void check(controller.signal);
    const id = window.setInterval(() => void check(controller.signal), VERSION_POLL_MS);
    const onVisible = (): void => {
      if (document.visibilityState === "visible") void check(controller.signal);
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      controller.abort();
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [check]);

  return deployed;
}
